/**
 * 下注操作区的**纯逻辑**：主按钮推导、指数滑块映射、档位磁吸、金额格式化（§11.5）。
 *
 * 单独成文件的理由：这些是整个客户端里唯一「算错就会真的输钱」的代码，必须能在
 * node 环境下直接单测，不能和 React / DOM 纠缠在一起。组件只负责把这里的结果贴到 DOM 上。
 *
 * 两条贯穿全文的红线：
 *  - #21：能否加注**只看 `LegalActions.canRaise`**。`minRaiseTo < maxRaiseTo` 在
 *    「前面有人不完整全下、加注权未重开」时仍然成立，按它渲染会给出一个必被引擎拒绝的按钮。
 *  - #18：金额语义恒为 raise-to（本轮累计投入总额）。所以滑块的两端是 minRaiseTo / maxRaiseTo，
 *    文案是「加注**到** X」。唯一显示增量的地方是跟注按钮（那笔钱此刻就要离开筹码堆），
 *    而 call 意图本身不带 `to` 字段，不存在语义混淆。
 */

import {
  SIZING_FRACTIONS,
  snapAmount,
  type Chips,
  type LegalActions,
  type SizingKind,
} from '@poker/shared'

// ============================================================================
// 常量（§11.5 防误触与手感）
// ============================================================================

/** All-in 长按确认时长 */
export const HOLD_CONFIRM_MS = 450
/** 中途松手的回滚时长 */
export const HOLD_ROLLBACK_MS = 180
/** `actionNo` 变化后主按钮的禁用冷却：防止对手加注后你正好点在原本的「跟注」位置 */
export const ACTION_COOLDOWN_MS = 220
/** 能过牌时弃牌只留在逃生口里，且要按更久 */
export const ESCAPE_FOLD_HOLD_MS = 600
/** 档位在轨道上的磁吸半径（CSS 像素） */
export const MAGNET_PX = 8
/** 原生 range 的整数刻度数：1000 档在 390px 宽的屏上远超像素精度 */
export const SLIDER_STEPS = 1000

// ============================================================================
// 金额格式化
// ============================================================================

/** 千分位。刻意不用 toLocaleString：某些 locale 会插入不换行空格，等宽数字列会抖。 */
export function formatChips(n: number): string {
  const v = Math.round(n)
  const digits = String(Math.abs(v))
  let out = ''
  for (let i = 0; i < digits.length; i++) {
    if (i > 0 && (digits.length - i) % 3 === 0) out += ','
    out += digits[i] ?? ''
  }
  return v < 0 ? `-${out}` : out
}

/**
 * 底池赔率百分比。toCall 为 0 时没有赔率可言。
 *
 * 目前**不进 UI**（见 buildDockPlan）：它是牌手自己会算的东西，印在操作区里只是旁白。
 * 保留计算是因为回放/统计要用。
 */
export function potOddsPct(legal: LegalActions): number | null {
  if (legal.toCall <= 0) return null
  const denom = legal.potForSizing + legal.toCall
  if (denom <= 0) return null
  return Math.round((legal.toCall / denom) * 100)
}

// ============================================================================
// 主按钮行推导（§11.5 的表格逐行实现）
// ============================================================================

export type DockButtonKind = 'fold' | 'check' | 'call' | 'raise'
export type DockTone = 'fold' | 'check' | 'call' | 'raise' | 'allin'
export type AllinConfirmMode = 'longpress' | 'doubletap' | 'off'

export interface DockButton {
  readonly kind: DockButtonKind
  readonly label: string
  /** 按钮上那个大数字；null = 不显示金额 */
  readonly amount: Chips | null
  /** 金额是「本轮累计总额(to)」还是「此刻付出的增量(delta)」，供 aria 文案区分 */
  readonly amountSemantics: 'to' | 'delta' | null
  readonly sub: string | null
  /** 该按钮占几格（总宽恒为 3 格）；隐藏的格子由剩下的按钮吃掉 */
  readonly span: number
  readonly tone: DockTone
  /** 需要长按二次确认（All-in） */
  readonly confirm: boolean
  /** true = 直接提交意图；false = 打开加注面板 */
  readonly submits: boolean
}

export interface DockPlan {
  readonly buttons: readonly DockButton[]
  /** 右侧加注位被隐藏时的副标，例如「本轮不可再加注」 */
  readonly note: string | null
  /** 打开加注面板时滑块的初始值（raise-to） */
  readonly raiseDefaultTo: Chips
  readonly canOpenRaise: boolean
  /** 主行里没有 Fold（toCall === 0），弃牌要走逃生口 */
  readonly escapeFold: boolean
}

export interface DockPlanOptions {
  readonly allinConfirm?: AllinConfirmMode
}

/**
 * 一个 raise-to 是否等于全下。
 *
 * 判据必须是 `to >= maxRaiseTo` 而**不是** `to >= stack`：`to` 含本街已投入而 `stack` 不含，
 * 本街已投 200、剩余 500 时「加注到 600」会被 `to >= stack` 误判成全下（§11.5 防误触第 2 条）。
 */
export function isAllInTo(to: Chips, legal: LegalActions): boolean {
  return to >= legal.maxRaiseTo
}

/**
 * 打开加注面板时的默认金额：优先半池（最常用、也最不容易点错），
 * 其次第一个非全下档位，都没有就落到最小加注。**永远不会自作主张选全下。**
 */
export function defaultRaiseTo(legal: LegalActions): Chips {
  if (!legal.canRaise) return 0
  const presets = legal.sizingPresets
  const half = presets.find((p) => p.kind === 'half')
  const firstNonAllIn = presets.find((p) => p.kind !== 'allIn')
  const picked = half?.to ?? firstNonAllIn?.to ?? legal.minRaiseTo
  return Math.min(Math.max(picked, legal.minRaiseTo), legal.maxRaiseTo)
}

function raiseBlockedNote(legal: LegalActions): string | null {
  switch (legal.raiseBlockedReason) {
    case 'notReopened':
      return '本轮不可再加注'
    case 'noOpponentCanCall':
      return '对手已全下，不能再加注'
    case 'noChips':
      return null // 这一行恒与 callIsAllIn 同时出现，再挂副标只是噪音
    default:
      return null
  }
}

/**
 * 由 `LegalActions` 推出主按钮行。`legal === null || !onAction` 时返回 null（不是自己的回合）。
 *
 * 注意 `canCheck ⟺ toCall === 0 ⟺ !canCall`：轮到你时 `stack > 0` 必然成立
 * （stack 为 0 的座位 status 不是 'active'，legalActions 会直接返回 blocked），
 * 所以这三者严格等价，下面据此只判 canCheck 一次。
 */
export function buildDockPlan(
  legal: LegalActions | null,
  opts: DockPlanOptions = {},
): DockPlan | null {
  if (legal === null || !legal.onAction) return null

  const needConfirm = (opts.allinConfirm ?? 'longpress') !== 'off'
  const buttons: DockButton[] = []

  // ① 左：弃牌。toCall === 0 时**不渲染**——免费弃牌是最昂贵的一类误操作（§11.5 防误触第 1 条）
  const escapeFold = legal.canCheck
  if (!escapeFold) {
    buttons.push({
      kind: 'fold',
      label: '弃牌',
      amount: null,
      amountSemantics: null,
      sub: null,
      span: 1,
      tone: 'fold',
      confirm: false,
      submits: true,
    })
  }

  // ② 中：过牌 / 跟注
  if (legal.canCheck) {
    buttons.push({
      kind: 'check',
      label: '过牌',
      amount: null,
      amountSemantics: null,
      sub: null,
      span: 1,
      tone: 'check',
      confirm: false,
      submits: true,
    })
  } else {
    // 只在真会误解时才多说一句：本轮已投过注时，按钮上的增量不等于本轮总额。
    // 底池赔率不在这里说 —— 不说也不会有人点错，属于纯噪音。
    const sub = legal.callTo !== legal.toCall ? `本轮共 ${formatChips(legal.callTo)}` : null
    buttons.push(
      legal.callIsAllIn
        ? {
            kind: 'call',
            label: 'ALL-IN 跟注',
            // 全下跟注付的是「剩余筹码」而不是 toCall（toCall 可能超过筹码），
            // 而剩余筹码没法从 LegalActions 单独还原。callTo 此时恒等于 maxRaiseTo，
            // 显示这个「本轮总额」既准确又是玩家真正关心的数字。
            amount: legal.callTo,
            amountSemantics: 'to',
            sub: needConfirm ? '长按确认' : null,
            span: 1,
            tone: 'allin',
            confirm: needConfirm,
            submits: true,
          }
        : {
            kind: 'call',
            label: '跟注',
            amount: legal.toCall,
            amountSemantics: 'delta',
            sub,
            span: 1,
            tone: 'call',
            confirm: false,
            submits: true,
          },
    )
  }

  // ③ 右：加注。★ 红线 #21：唯一判据是 canRaise
  let raiseDefaultTo = 0
  if (legal.canRaise) {
    raiseDefaultTo = defaultRaiseTo(legal)
    // 过了 canRaise 这道门之后，min/max 只用来判断「可选金额是否唯一」——
    // 这是纯 UI 取舍（要不要展开面板），与「能否加注」无关，不违反 #21。
    const singleAmount = legal.minRaiseTo >= legal.maxRaiseTo
    const allIn = isAllInTo(raiseDefaultTo, legal)
    buttons.push(
      singleAmount
        ? {
            kind: 'raise',
            label: 'ALL-IN',
            amount: legal.maxRaiseTo,
            amountSemantics: 'to',
            sub: needConfirm ? '长按确认' : null,
            span: 1,
            tone: 'allin',
            confirm: needConfirm,
            submits: true,
          }
        : {
            kind: 'raise',
            label: legal.raiseKind === 'bet' ? '下注' : '加注到',
            amount: raiseDefaultTo,
            amountSemantics: 'to',
            sub: null,
            span: 1,
            tone: allIn ? 'allin' : 'raise',
            confirm: false, // 打开面板不需要确认，确认在面板里的提交键上
            submits: false,
          },
    )
  }

  // ④ 空出来的格子给「最该被点中」的按钮：加注 > 跟注/过牌 > 弃牌
  const extra = 3 - buttons.length
  const spans = buttons.map(() => 1)
  if (extra > 0) {
    let target = buttons.findIndex((b) => b.kind === 'raise')
    if (target < 0) target = buttons.findIndex((b) => b.kind === 'call' || b.kind === 'check')
    if (target < 0) target = 0
    spans[target] = (spans[target] ?? 1) + extra
  }

  return {
    buttons: buttons.map((b, i) => ({ ...b, span: spans[i] ?? 1 })),
    // 只有「能跟注但不能加注」这一行需要解释；ALL-IN 跟注那行右侧本来就该是空的
    note: !legal.canRaise && legal.canCall && !legal.callIsAllIn ? raiseBlockedNote(legal) : null,
    raiseDefaultTo,
    canOpenRaise: legal.canRaise && legal.minRaiseTo < legal.maxRaiseTo,
    escapeFold,
  }
}

// ============================================================================
// 指数滑块映射（§11.5）
// ============================================================================

function clamp(lo: number, v: number, hi: number): number {
  return Math.min(Math.max(v, lo), hi)
}

export interface SliderMapping {
  /** `canRaise && maxRaiseTo > minRaiseTo`。false 时整条轨道禁用。 */
  readonly enabled: boolean
  readonly min: Chips
  readonly max: Chips
  readonly k: number
  /** t ∈ [0,1] → raise-to（已取整并钳位） */
  toAmount(t: number): Chips
  /** raise-to → t ∈ [0,1] */
  toT(amount: Chips): number
}

/**
 * min 到全下常跨 50–200 倍，线性滑块的小额区（最常用）只占几个像素，不可控。
 * 用指数映射把低端拉开：
 *   K = clamp(1.2, ln(max/min), 3.5)
 *   amount(t) = min + (max-min) · (e^{Kt}-1)/(e^K-1)
 *
 * `enabled === false` 时（短码只能全下）两端相等，必须整条禁用：
 * 否则 `(a-min)/max(1, max-min)` 恒为 0，反解出的把手位置虽不是 NaN 也毫无意义，
 * 而 `ln(max/min)` 在 min 为 0（canRaise=false）时直接是 Infinity。
 */
export function createSliderMapping(legal: LegalActions, bigBlind: Chips): SliderMapping {
  const min = legal.minRaiseTo
  const max = legal.maxRaiseTo
  const enabled = legal.canRaise && max > min
  const k = enabled ? clamp(1.2, Math.log(max / Math.max(1, min)), 3.5) : 1.2
  const expK = Math.exp(k) - 1

  return {
    enabled,
    min,
    max,
    k,
    toAmount(t: number): Chips {
      if (!enabled) return max
      const tt = clamp(0, t, 1)
      // 末端直接返回 max：取整步长可能把 max 向下取到 max-step，那样滑块永远够不到全下
      if (tt >= 1) return max
      const raw = min + (max - min) * ((Math.exp(k * tt) - 1) / expK)
      return snapAmount(raw, bigBlind, min, max)
    },
    toT(amount: Chips): number {
      if (!enabled) return 1
      const r = clamp(0, (amount - min) / Math.max(1, max - min), 1)
      return clamp(0, Math.log(r * expK + 1) / k, 1)
    },
  }
}

// ============================================================================
// 档位 pill 与磁吸
// ============================================================================

export interface PresetPill {
  readonly kind: SizingKind
  readonly label: string
  /** null = 该档位不可用（原始金额低于最小加注） */
  readonly to: Chips | null
  readonly disabled: boolean
  readonly hint: string | null
}

const PRESET_LABEL: Readonly<Record<SizingKind, string>> = {
  third: '⅓ 池',
  half: '½ 池',
  twoThirds: '⅔ 池',
  pot: '满池',
  allIn: '全下',
}

export function presetLabel(kind: SizingKind): string {
  return PRESET_LABEL[kind]
}

/**
 * 档位 pill 列表。金额**一律取自 `legal.sizingPresets`**（服务端已去重、已合并全下、
 * 已剔除低于最小加注的档位），客户端只做 i18n 与置灰。
 *
 * 被剔除的档位分两种，处理方式不同：
 *  - 原始金额 < minRaiseTo：置灰保留，让玩家看见「⅓ 池不够一个最小加注」，**绝不静默上调**；
 *  - 原始金额 >= maxRaiseTo：已被合并进「全下」，再渲染一个同额 pill 只是重复，直接不渲染。
 *
 * `base` 的还原：canRaise 为真时 callIsAllIn 必为假，故 callTo === betToMatch，
 * 于是 committedThisRound = callTo - toCall，而 base = committed + toCall = callTo。
 */
export function buildPresetPills(legal: LegalActions): PresetPill[] {
  if (!legal.canRaise) return []
  const base = legal.callTo
  const potAfterCall = legal.potForSizing + legal.toCall
  const byKind = new Map<SizingKind, Chips>()
  for (const p of legal.sizingPresets) if (!byKind.has(p.kind)) byKind.set(p.kind, p.to)

  const pills: PresetPill[] = []
  for (const { kind, fraction } of SIZING_FRACTIONS) {
    const to = byKind.get(kind)
    if (to !== undefined) {
      pills.push({ kind, label: PRESET_LABEL[kind], to, disabled: false, hint: null })
      continue
    }
    const raw = base + fraction * potAfterCall
    if (raw < legal.minRaiseTo) {
      pills.push({ kind, label: PRESET_LABEL[kind], to: null, disabled: true, hint: '不足最小加注' })
    }
    // raw >= maxRaiseTo：已并入「全下」，不重复渲染
  }
  const allIn = byKind.get('allIn')
  if (allIn !== undefined) {
    pills.push({ kind: 'allIn', label: PRESET_LABEL.allIn, to: allIn, disabled: false, hint: null })
  }
  return pills
}

export interface MagnetResult {
  readonly to: Chips
  /** 命中的档位；null = 落在档位之间 */
  readonly kind: SizingKind | null
}

/**
 * 轨道上的 ±8px 磁吸。阈值换算成 t 用的是 ResizeObserver 缓存的轨道宽度，
 * 不在拖动回调里读 `getBoundingClientRect`（红线 #47 同源的理由：拖动是每帧路径）。
 */
export function applyMagnet(
  t: number,
  pills: readonly PresetPill[],
  mapping: SliderMapping,
  magnetT: number,
): MagnetResult {
  let best: { kind: SizingKind; to: Chips; d: number } | null = null
  for (const p of pills) {
    if (p.disabled || p.to === null) continue
    const d = Math.abs(mapping.toT(p.to) - t)
    if (d <= magnetT && (best === null || d < best.d)) best = { kind: p.kind, to: p.to, d }
  }
  if (best !== null) return { to: best.to, kind: best.kind }
  return { to: mapping.toAmount(t), kind: null }
}

/** 手动输入的落位：取整到筹码步长并钳进 [minRaiseTo, maxRaiseTo]。空串/非数字返回 null。 */
export function parseManualAmount(
  raw: string,
  legal: LegalActions,
  bigBlind: Chips,
): Chips | null {
  const digits = raw.replace(/[^\d]/g, '')
  if (digits.length === 0) return null
  const n = Number.parseInt(digits, 10)
  if (!Number.isFinite(n)) return null
  return snapAmount(n, bigBlind, legal.minRaiseTo, legal.maxRaiseTo)
}
